import { forwardRef, useRef, type ReactNode } from "react";
import { Search, X } from "lucide-react";
import { Input } from "./Input";
import { Spinner } from "./primitives";

/**
 * The search box every list page and picker was hand-rolling.
 *
 * Three things were missing everywhere, and each was reported as its own bug:
 * there was no way to clear a term except selecting the text and deleting it; a
 * search in flight looked identical to a search that found nothing; and each page
 * repeated the icon-and-placeholder boilerplate slightly differently.
 *
 * The clear button returns focus to the field, because the reason someone clears
 * a search is almost always to type a different one.
 */
export const SearchInput = forwardRef<
  HTMLInputElement,
  {
    value: string;
    onChange: (next: string) => void;
    /** Visible label. Omit for a bare box and pass `ariaLabel` instead. */
    label?: string;
    /** Accessible name when there is no visible label. */
    ariaLabel?: string;
    placeholder?: string;
    hint?: string;
    /**
     * True while the search is in flight. Pass `query.isFetching`, not
     * `isLoading`: callers keep previous rows via `placeholderData`, so
     * `isLoading` is true only before the first fetch ever.
     */
    loading?: boolean;
    autoFocus?: boolean;
    disabled?: boolean;
    className?: string;
    leadingIcon?: ReactNode;
  }
>(function SearchInput(
  {
    value,
    onChange,
    label,
    ariaLabel,
    placeholder = "Search…",
    hint,
    loading = false,
    autoFocus = false,
    disabled = false,
    className,
    leadingIcon,
  },
  forwardedRef,
) {
  const localRef = useRef<HTMLInputElement | null>(null);

  /* The clear button needs the element to refocus it, and callers may also want
     a ref, so both are populated from one callback. */
  const setRef = (node: HTMLInputElement | null): void => {
    localRef.current = node;
    if (typeof forwardedRef === "function") forwardedRef(node);
    else if (forwardedRef !== null) forwardedRef.current = node;
  };

  /* Spinner takes precedence over the clear button: while a search runs, the
     activity is the more useful thing to show in that one slot, and the term is
     still clearable with the keyboard. */
  const trailingSlot = loading ? (
    <Spinner label="Searching" className="text-content-muted" />
  ) : value === "" ? undefined : (
    <button
      type="button"
      aria-label="Clear search"
      title="Clear search"
      onClick={() => {
        onChange("");
        localRef.current?.focus();
      }}
      className="flex size-6 items-center justify-center rounded text-content-muted transition-colors hover:bg-[var(--surface-hover)] hover:text-content focus:outline-none focus:ring-2 focus:ring-[color-mix(in_oklab,var(--ring-focus)_20%,transparent)]"
    >
      <X aria-hidden className="size-3.5" />
    </button>
  );

  return (
    <Input
      ref={setRef}
      /* Deliberately "text", not "search": WebKit and Chrome draw their own
         cancel button on a search input, which would sit next to ours. */
      type="text"
      {...(label !== undefined && { label })}
      {...(label === undefined && ariaLabel !== undefined && { "aria-label": ariaLabel })}
      {...(hint !== undefined && { hint })}
      placeholder={placeholder}
      autoFocus={autoFocus}
      disabled={disabled}
      className={className}
      leadingIcon={leadingIcon ?? <Search className="size-4" />}
      trailingSlot={trailingSlot}
      value={value}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    />
  );
});
